/* eslint-disable max-lines -- Figma-exported illustration; the length is SVG path/filter data, not logic */
import { FormattedMessage, useIntl } from 'react-intl';

import type { SVGProps } from 'react';

/**
 * Preview illustration for the Scribe onboarding modal: the Heidi logo
 * transcribing (green "Transcribe" pill), a Templates card, and a generated
 * SOAP note with Transcript / Note / Referral letter tabs. Converted from the
 * Figma export (scribe-onboarding-modal.svg) so the labels can be translated
 * via react-intl. The artwork crops the note card: the SOAP note body is a
 * demo snippet that is intentionally cut off mid-sentence by the card edge.
 */
// theme-agnostic: fixed-palette Figma illustration, renders identically in both themes
export const ScribeOnboardingModalSvg = (props: SVGProps<SVGSVGElement>) => {
  const intl = useIntl();

  return (
    // direction="ltr": the artwork is composed left-to-right; without it, an
    // inherited dir="rtl" (Arabic) flips text-anchor's start side and every
    // label extends leftward from its authored x, clipping out of the canvas.
    <svg
      viewBox="0 0 452 538"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      direction="ltr"
      role="img"
      aria-label={intl.formatMessage({
        defaultMessage:
          'Preview of Heidi transcribing a session and turning it into a SOAP note with templates',
        id: 'Z9j30zfPfm',
      })}
      {...props}
    >
      <circle cx="102.312" cy="112" r="70.5" stroke="#755760" />
      <circle cx="103.812" cy="113.5" r="50.3" stroke="#755760" strokeWidth="1.6" />
      <rect x="67.3125" y="77" width="72" height="72" rx="36" fill="#4C2934" />
      <path
        d="M117.031 108.567C115.461 108.983 117.031 111.992 115.347 112.181C114.855 112.238 114.022 112.181 113.189 112.2C111.373 112.087 110.351 112.522 110.199 111.727C110.161 108.68 110.199 103.457 110.199 100.75C110.37 98.0818 109.613 95.2429 107.322 93.5964C103.67 90.7196 97.765 92.8583 96.743 97.4004C96.0238 101.791 96.6484 107.336 96.4213 111.727C96.3077 112.579 94.3962 112.068 93.431 112.2C90.895 112.2 90.3461 112.598 90.365 109.91C90.3272 109.153 89.7973 108.302 89.0402 108.756C88.1507 109.608 88.6239 111.443 88.5103 112.617C88.1886 119.771 93.5256 126.679 100.528 127.852C101.058 127.965 101.834 128.022 102.118 128.476C102.761 129.29 101.834 133.038 102.894 133.624C103.973 134.06 104.332 133.057 104.257 131.542C104.389 129.858 103.764 128.268 105.411 127.965C108.572 127.473 111.713 125.94 114.022 123.442C117.637 119.79 118.564 114.32 117.997 109.418C117.883 108.888 117.627 108.567 117.268 108.529L117.031 108.567ZM98.4653 98.6306C98.9762 94.5616 104.862 92.7636 107.171 96.492C108.799 98.9145 108.174 102.624 108.307 105.993C108.042 109.551 109.215 113.506 106.887 116.478C104.029 119.922 98.4653 117.462 98.3895 113.166C98.2192 108.907 98.1624 103.305 98.4085 98.9334L98.4653 98.6306ZM112.262 122.61C105.354 129.498 91.8034 125.354 90.5732 115.815C90.5164 115.228 90.6111 114.774 90.9328 114.49C91.7655 113.866 94.2448 113.998 95.5696 114.32C96.3834 114.623 96.7052 115.475 97.1215 116.232C99.8279 121.682 107.909 120.906 109.953 115.285C110.767 113.809 112.622 114.112 114.117 114.093C118.791 113.96 113.928 121.058 112.508 122.382L112.281 122.61H112.262Z"
        fill="#F9F4F1"
        stroke="#F9F4F1"
        strokeWidth="0.69312"
      />
      <path
        d="M29.3125 16C29.3125 7.16344 36.4759 0 45.3125 0H162.312C171.149 0 178.312 7.16344 178.312 16V32C178.312 40.8366 171.149 48 162.312 48H45.3125C36.4759 48 29.3125 40.8366 29.3125 32V16Z"
        fill="#16A34A"
      />
      <g clipPath="url(#scribe-onboarding-modal-clip0)">
        <path
          d="M46.4792 22.3333V24.8333M49.8126 19V28.1667M53.1459 16.5V31.5M56.4792 20.6667V26.5M59.8126 18.1667V29M63.1459 22.3333V24.8333"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <text
        fill="white"
        style={{ whiteSpace: 'pre' }}
        xmlSpace="preserve"
        className="font-body"
        fontSize="18"
        fontWeight="500"
        letterSpacing="0em"
      >
        <tspan x="70.8125" y="30.5909">
          <FormattedMessage
            defaultMessage="Transcribe"
            id="DhP+41HHqO"
            description="Label on the green recording pill in the Scribe onboarding illustration — Heidi is transcribing the session (verb). Demo text snippet from a screenshot. Keep short: the pill has fixed width"
          />
        </tspan>
      </text>
      <g filter="url(#scribe-onboarding-modal-filter0)">
        <g clipPath="url(#scribe-onboarding-modal-clip1)">
          <rect x="194" y="46.4258" width="201" height="212" rx="16" fill="white" />
          <text
            fill="#211217"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-exposure"
            fontSize="20"
            letterSpacing="-0.025em"
          >
            <tspan x="214" y="77.9277">
              <FormattedMessage
                defaultMessage="Templates"
                id="6hhb71Gntj"
                description="Heading of the templates card in the Scribe onboarding illustration — the user's note templates. Demo text snippet from a screenshot. Keep short: the card has fixed width"
              />
            </tspan>
          </text>
          <circle cx="218" cy="100.426" r="8" fill="#D4C4C9" />
          <rect x="234" y="96.4258" width="115" height="8" rx="4" fill="#D4C4C9" />
          <circle cx="218" cy="126.426" r="8" fill="#D4C4C9" />
          <rect x="234" y="122.426" width="115" height="8" rx="4" fill="#D4C4C9" />
          <circle cx="218" cy="152.426" r="8" fill="#D4C4C9" />
          <rect x="234" y="148.426" width="115" height="8" rx="4" fill="#D4C4C9" />
        </g>
      </g>
      <g filter="url(#scribe-onboarding-modal-filter1)">
        <g clipPath="url(#scribe-onboarding-modal-clip2)">
          <rect x="32" y="152.575" width="388" height="341" rx="16" fill="#F9F4F1" />
          <g clipPath="url(#scribe-onboarding-modal-clip3)">
            <path
              d="M66.1667 192.908V195.408M69.5001 189.575V198.741M72.8334 187.075V202.075M76.1667 191.241V197.075M79.5001 188.741V199.575M82.8334 192.908V195.408"
              stroke="#876C74"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <text
            fill="#876C74"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-body"
            fontSize="14"
            fontWeight="500"
            letterSpacing="0em"
          >
            <tspan x="90.5" y="199.757">
              <FormattedMessage
                defaultMessage="Transcript"
                id="4lc4d13FD+"
                description="Tab label in the Scribe onboarding illustration, alongside 'Note' and 'Referral letter' — the session transcript. Demo text snippet from a screenshot. Keep short: limited space"
              />
            </tspan>
          </text>
          <path
            d="M176 184.575C176 180.156 179.582 176.575 184 176.575H251C255.418 176.575 259 180.156 259 184.575V204.575C259 208.993 255.418 212.575 251 212.575H184C179.582 212.575 176 208.993 176 204.575V184.575Z"
            fill="white"
          />
          <path
            d="M176 184.575C176 180.156 179.582 176.575 184 176.575H251C255.418 176.575 259 180.156 259 184.575V204.575C259 208.993 255.418 212.575 251 212.575H184C179.582 212.575 176 208.993 176 204.575V184.575Z"
            stroke="#F4E7DD"
            strokeWidth="2"
          />
          <g clipPath="url(#scribe-onboarding-modal-clip4)">
            <path
              d="M198 201.241H205.5M200.5 188.741L203 191.241M201.75 187.491C202.082 187.16 202.531 186.974 203 186.974C203.232 186.974 203.462 187.019 203.676 187.108C203.891 187.197 204.086 187.327 204.25 187.491C204.414 187.656 204.544 187.85 204.633 188.065C204.722 188.279 204.768 188.509 204.768 188.741C204.768 188.974 204.722 189.203 204.633 189.418C204.544 189.632 204.414 189.827 204.25 189.991L193.833 200.408L190.5 201.241L191.333 197.908L201.75 187.491Z"
              stroke="#3B82F6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <text
            fill="#211217"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-body"
            fontSize="14"
            fontWeight="500"
            letterSpacing="0em"
          >
            <tspan x="214" y="199.757">
              <FormattedMessage
                defaultMessage="Note"
                id="aWBGePTQ69"
                description="Tab label in the Scribe onboarding illustration, alongside 'Transcript' and 'Referral letter' — the clinical note (noun). Demo text snippet from a screenshot. Keep short: the tab has fixed width"
              />
            </tspan>
          </text>
          <g clipPath="url(#scribe-onboarding-modal-clip5)">
            <path
              d="M290.667 186.241V189.575C290.667 190.017 290.842 190.44 291.155 190.753C291.467 191.066 291.891 191.241 292.333 191.241H295.667M291.5 186.241H284C283.558 186.241 283.134 186.417 282.821 186.729C282.509 187.042 282.333 187.466 282.333 187.908V201.241C282.333 201.683 282.509 202.107 282.821 202.42C283.134 202.732 283.558 202.908 284 202.908H294C294.442 202.908 294.866 202.732 295.178 202.42C295.491 202.107 295.667 201.683 295.667 201.241V190.408L291.5 186.241Z"
              stroke="#876C74"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <text
            fill="#876C74"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-body"
            fontSize="14"
            fontWeight="500"
            letterSpacing="0em"
          >
            <tspan x="305" y="199.757">
              <FormattedMessage
                defaultMessage="Referral letter"
                id="xoDnNKaqOQ"
                description="Tab label in the Scribe onboarding illustration, alongside 'Transcript' and 'Note' — a letter referring the patient to another clinician. Demo text snippet from a screenshot — the artwork is cropped, so the end of the label may run off the card. Keep short: limited space"
              />
            </tspan>
          </text>
          <rect x="57" y="221.575" width="493" height="1006" rx="15" fill="white" />
          <rect
            x="57"
            y="221.575"
            width="493"
            height="1006"
            rx="15"
            stroke="#F4E7DD"
            strokeWidth="2"
          />
          <path
            d="M88 245.075H145C149.142 245.075 152.5 248.433 152.5 252.575V280.575C152.5 284.717 149.142 288.075 145 288.075H88C83.8579 288.075 80.5 284.717 80.5 280.575V252.575C80.5 248.433 83.8579 245.075 88 245.075Z"
            fill="white"
          />
          <path
            d="M88 245.075H145C149.142 245.075 152.5 248.433 152.5 252.575V280.575C152.5 284.717 149.142 288.075 145 288.075H88C83.8579 288.075 80.5 284.717 80.5 280.575V252.575C80.5 248.433 83.8579 245.075 88 245.075Z"
            stroke="#F4E7DD"
          />
          <text
            fill="#211217"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-body"
            fontSize="18"
            fontWeight="500"
            letterSpacing="0em"
          >
            <tspan x="92" y="273.166">
              <FormattedMessage
                defaultMessage="SOAP"
                id="uRviYYQyGm"
                description="Template name chip on the mock note in the Scribe onboarding illustration — the SOAP (Subjective, Objective, Assessment, Plan) note format. Usually kept as-is across languages. Keep short: the chip has fixed width"
              />
            </tspan>
          </text>
          <text
            fill="#211217"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-body"
            fontSize="16"
            fontWeight="600"
            letterSpacing="0em"
          >
            <tspan x="80" y="322.393">
              <FormattedMessage
                defaultMessage="S - Subjective"
                id="I9rhqHsIgH"
                description="Section heading of the mock SOAP note in the Scribe onboarding illustration — the Subjective section (the S in SOAP). Demo text snippet from a screenshot. Keep short: limited space"
              />
            </tspan>
          </text>
          <foreignObject x="80" y="342" width="340" height="170">
            <div
              dir="auto"
              className="font-body"
              style={{ fontSize: '16px', lineHeight: '24px', color: '#211217' }}
            >
              <FormattedMessage
                defaultMessage="Sam presents with a 4-day history of feeling unwell, initially starting with a sore throat, followed by fever, generalized body aches, fatigue, and a mild headache.{br}{br}Denies cough, shortness of breath, or chest pain.{br}Has been self-medicating with paracetamol, which provides temporary relief of fever, though symptoms recur once it wears off."
                id="T+kY5mgIiA"
                description="Body of the mock SOAP note in the Scribe onboarding illustration about a fictional patient, Sam. Keep the {br} placeholders as line breaks. Demo text snippet from a screenshot: the card is cropped, so the text is intentionally cut off partway — it does not need to read as a complete note and may end mid-sentence. Keep the translation compact"
                values={{ br: <br /> }}
              />
            </div>
          </foreignObject>
        </g>
      </g>
      <path
        d="M382.678 31.7769C382.678 37.852 387.603 42.7769 393.678 42.7769H415.259C416.17 42.7769 416.908 43.5151 416.908 44.4258C416.908 45.3365 416.17 46.0747 415.259 46.0747H393.678C387.603 46.0747 382.678 50.9996 382.678 57.0747V78.6504C382.678 79.5611 381.94 80.2993 381.029 80.2993C380.118 80.2993 379.38 79.5611 379.38 78.6504V57.0747C379.38 50.9996 374.455 46.0747 368.38 46.0747H346.799C345.888 46.0747 345.15 45.3365 345.15 44.4258C345.15 43.5151 345.888 42.7769 346.799 42.7769H368.38C374.455 42.7769 379.38 37.852 379.38 31.7769V10.2012C379.38 9.29049 380.118 8.55225 381.029 8.55225C381.94 8.55225 382.678 9.2905 382.678 10.2012V31.7769Z"
        fill="#4C2934"
      />
      <defs>
        <filter
          id="scribe-onboarding-modal-filter0"
          x="162"
          y="26.4258"
          width="265"
          height="276"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="12" />
          <feGaussianBlur stdDeviation="16" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.156863 0 0 0 0 0.0117647 0 0 0 0 0.0588235 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <filter
          id="scribe-onboarding-modal-filter1"
          x="0"
          y="132.575"
          width="452"
          height="405"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="12" />
          <feGaussianBlur stdDeviation="16" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.156863 0 0 0 0 0.0117647 0 0 0 0 0.0588235 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <clipPath id="scribe-onboarding-modal-clip0">
          <rect width="20" height="20" fill="white" transform="translate(44.8125 14)" />
        </clipPath>
        <clipPath id="scribe-onboarding-modal-clip1">
          <rect x="194" y="46.4258" width="201" height="212" rx="16" fill="white" />
        </clipPath>
        <clipPath id="scribe-onboarding-modal-clip2">
          <rect x="32" y="152.575" width="388" height="341" rx="16" fill="white" />
        </clipPath>
        <clipPath id="scribe-onboarding-modal-clip3">
          <rect width="20" height="20" fill="white" transform="translate(64.5 184.575)" />
        </clipPath>
        <clipPath id="scribe-onboarding-modal-clip4">
          <rect width="20" height="20" fill="white" transform="translate(188 184.575)" />
        </clipPath>
        <clipPath id="scribe-onboarding-modal-clip5">
          <rect width="20" height="20" fill="white" transform="translate(279 184.575)" />
        </clipPath>
      </defs>
    </svg>
  );
};
